import React from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';

/**
 * Entry Node Component
 *
 * Represents the entry point into a nested child graph. Entry nodes are special
 * marker nodes that are automatically created when a container node's child graph
 * is initialized. They serve as the destination for connections coming from the
 * parent graph through the container's input pins.
 *
 * **Design:**
 * - Slim rectangular marker node positioned on the left side of the canvas
 * - Always has exactly one output handle (to connect internally within the child graph)
 * - Number of Entry nodes matches the number of input pins on the parent container
 * - Non-deletable while referenced by connections (enforced by the API)
 * - Read-only node (cannot edit properties)
 *
 * **Semantics:**
 * When a traversal enters a container through input pin i, execution continues
 * at Entry node i inside the child graph. This enables clean separation of concerns
 * between parent and child graphs while maintaining flow continuity.
 *
 * **Example Use Case:**
 * A dialogue container has 2 inputs (from NPC and from Player). Opening the
 * container shows 2 Entry nodes, one for each input source. Internal dialogue
 * flow connects from these Entry nodes.
 *
 * @component
 * @example
 * // Automatically created by the system when opening a container
 * const entryNode: Node = {
 *   id: 'entry_0',
 *   type: 'entry',
 *   position: { x: 0, y: 0 },
 *   data: { index: 0 },
 * };
 */
export const EntryNode: React.FC<NodeProps> = ({ data }) => {
    const index = data?.index ?? 0;

    return (
        <div className="entry-node">
            {/* Output handle: connects to internal graph nodes */}
            <Handle type="source" position={Position.Right} id="out" />

            {/* Visual indicator */}
            <div className="entry-node-content">
                <span className="entry-node-label">Entry {index}</span>
            </div>
        </div>
    );
};

/**
 * Entry Node Metadata
 *
 * Defines the schema, display properties, and default data for Entry nodes.
 * This metadata is used by the NodeTypeRegistry to register the node type
 * and make it available in the system.
 *
 * @constant
 */
export const entryNodeMeta = {
    type: 'entry' as const,
    label: 'Entry',
    icon: '↷',
    defaultData: {
        index: 0,
    },
    description: 'Entry point for a nested graph (auto-created)',
    category: 'flow-control',
};
